/**
 * useTmdbImages -- Hook for fetching TMDB canonical artwork.
 *
 * Calls /api/streaming/tmdb/images when a TMDB ID is available,
 * returning the best poster and backdrop file paths for proxy URLs.
 */

import { useEffect, useState } from "react";

import { useCoverArtContext } from "../contexts/CoverArtSettingsContext";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TmdbImageData {
  bestPoster: { filePath: string; width: number; height: number } | null;
  bestBackdrop: { filePath: string; width: number; height: number } | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// URL Builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a proxy URL for a TMDB image.
 *
 * @param filePath - TMDB file path (e.g. "/abc123.jpg")
 * @param type - "poster" | "backdrop" | "still"
 * @param maxWidth - Desired max width in pixels
 */
export function buildTmdbProxyUrl(
  filePath: string,
  type: "poster" | "backdrop" | "still",
  maxWidth: number,
): string {
  const params = new URLSearchParams({
    path: filePath,
    type,
    maxWidth: String(maxWidth),
  });
  return `/api/streaming/tmdb/image?${params.toString()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch TMDB canonical images for a media item.
 *
 * @param tmdbId - TMDB ID from providerIds, or null/undefined if unavailable
 * @param mediaType - "movie" | "tv" | "tv_season" | "tv_episode"
 * @param season - Season number (for tv_season/tv_episode)
 * @param episode - Episode number (for tv_episode)
 */
export function useTmdbImages(
  tmdbId: string | null | undefined,
  mediaType: string | null | undefined,
  season?: number | null,
  episode?: number | null,
): { tmdbImages: TmdbImageData | null; tmdbLoading: boolean } {
  const { tmdbEnabled } = useCoverArtContext();
  const [tmdbImages, setTmdbImages] = useState<TmdbImageData | null>(null);
  const [tmdbLoading, setTmdbLoading] = useState(false);

  useEffect(() => {
    if (!tmdbEnabled || !tmdbId || !mediaType) {
      setTmdbImages(null);
      return;
    }

    let cancelled = false;
    setTmdbLoading(true);

    const params = new URLSearchParams({ tmdbId, mediaType });
    if (season != null) params.set("season", String(season));
    if (episode != null) params.set("episode", String(episode));

    fetch(`/api/streaming/tmdb/images?${params.toString()}`, {
      headers: { Accept: "application/json" },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((json: { ok?: boolean; data?: TmdbImageData }) => {
        if (cancelled) return;
        if (json.ok && json.data) {
          setTmdbImages(json.data);
        } else {
          setTmdbImages(null);
        }
      })
      .catch(() => {
        if (!cancelled) setTmdbImages(null);
      })
      .finally(() => {
        if (!cancelled) setTmdbLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tmdbEnabled, tmdbId, mediaType, season, episode]);

  return { tmdbImages, tmdbLoading };
}
